/**
 * JWT access + refresh token storage.
 *
 * Stored in `localStorage` so tokens are shared across tabs opened from the
 * same browser session. This allows links opened in new tabs to reuse the
 * existing session without forcing a re-login. Cross-tab refresh coordination
 * (preventing ROTATE_REFRESH_TOKENS race conditions) lives in AuthService.
 *
 * Refresh-token flow lands in Phase H (ADR-0045): every login response now
 * carries an access + refresh pair, the refresh-token interceptor calls
 * /auth/token/refresh/ on 401, and explicit logout blacklists the refresh
 * server-side.
 */

const ACCESS_TOKEN_KEY = 'egs:auth:access';
const REFRESH_TOKEN_KEY = 'egs:auth:refresh';

export class TokenStorage {
  static get(): string | null {
    if (typeof localStorage === 'undefined') return null;
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  }

  static getRefresh(): string | null {
    if (typeof localStorage === 'undefined') return null;
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  }

  /**
   * Store the access token, and optionally the refresh token. Callers that
   * pass only the access token (e.g., refresh interceptor when rotation
   * isn't enabled by the server response) preserve any existing refresh
   * value already in storage.
   */
  static set(accessToken: string, refreshToken?: string | null): void {
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    if (refreshToken) {
      localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    }
  }

  static clear(): void {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  }
}

export { ACCESS_TOKEN_KEY };
